import type { FieldChange } from '@domains/subscription/priv/components/Confirmable'
import { Stack, useTheme } from '@mui/material'
import { BillingList } from '../components/BillingList'
import { BorderedStack } from '../components/BorderedStack'
import type { FeatureData, FeatureRequest } from '../types'

export interface FeatureFormProps {
  feature: FeatureData
  onSubmit: (data: FeatureRequest<null>) => void
  onDelete?: (featureId: string) => void
  isLoading: boolean
  isFormDirty: (change: FieldChange) => boolean
  isDefaultedField?: boolean
  allowDeleteFeature?: boolean
}

export interface FeaturesProps {
  activeFeatures: FeatureData[]
  availableFeatures: FeatureData[]
  defaultFeatures?: FeatureData[]
  onSave: (featureData: FeatureRequest<null>) => void
  onDelete?: (featureId: string) => void
  isLoading: boolean
  selectedFeatureId: string | undefined
  onSelectFeature: (featureId: string) => void
  isFormDirty: (change: FieldChange) => boolean
  allowDeleteFeature?: boolean
  slots: {
    FeatureForm: React.ComponentType<FeatureFormProps>
  }
}

export const Features = ({
  availableFeatures,
  activeFeatures,
  defaultFeatures = [],
  selectedFeatureId,
  onSave,
  isLoading,
  onSelectFeature,
  isFormDirty,
  allowDeleteFeature,
  onDelete,
  slots,
}: FeaturesProps) => {
  const { palette } = useTheme()

  const selectedFeature = getSelectedFeature({ activeFeatures, defaultFeatures, availableFeatures, selectedFeatureId })

  const nonActiveAvaliableFeatures = availableFeatures.filter((feature) => !activeFeatures.some((active) => active.id === feature.id))
  const defaultedNonActiveFeatures = nonActiveAvaliableFeatures.map((f) => {
    const defaultFeature = defaultFeatures.find((df) => df.id === f.id)

    return defaultFeature ?? f
  })

  return (
    <Stack
      sx={{
        flex: 1,
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: 2,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <BorderedStack
        sx={{
          overflow: 'auto',
          flex: 1,
          padding: 3,
          gap: 4,
          bgcolor: palette.common.white,
          minHeight: 0,
        }}
      >
        <BillingList
          activeRowId={selectedFeatureId}
          sections={[
            {
              index: 0,
              title: 'Configured features',
              items: activeFeatures,
              onClick: onSelectFeature,
              active: true,
            },
            {
              index: 1,
              title: 'All Features',
              items: defaultedNonActiveFeatures,
              onClick: onSelectFeature,
              active: false,
            },
          ]}
        />
      </BorderedStack>
      <Stack
        sx={{
          position: 'sticky',
          top: (theme) => theme.spacing(2),
          flex: 1,
        }}
      >
        {selectedFeature?.feature && (
          <BorderedStack
            sx={{
              padding: 3,
              gap: 4,
              bgcolor: palette.common.white,
            }}
          >
            <slots.FeatureForm
              isLoading={isLoading}
              feature={selectedFeature.feature}
              onSubmit={onSave}
              isFormDirty={isFormDirty}
              isDefaultedField={selectedFeature.isDefault}
              allowDeleteFeature={allowDeleteFeature}
              onDelete={onDelete}
            />
          </BorderedStack>
        )}
      </Stack>
    </Stack>
  )
}

interface GetSelectedFeatureParams {
  activeFeatures: FeatureData[]
  availableFeatures: FeatureData[]
  defaultFeatures?: FeatureData[]
  selectedFeatureId: string | undefined
}

export const getSelectedFeature = ({
  activeFeatures,
  availableFeatures,
  selectedFeatureId,
  defaultFeatures = [],
}: GetSelectedFeatureParams) => {
  const matchActiveFeature = activeFeatures.find((f) => f.id === selectedFeatureId)
  const matchAvaliableFeature = availableFeatures.find((f) => f.id === selectedFeatureId)
  const matchDefaultFeature = defaultFeatures.find((f) => f.id === selectedFeatureId)

  if (matchActiveFeature) {
    return {
      feature: matchActiveFeature,
      isDefault: false,
    }
  }

  if (matchDefaultFeature) {
    return {
      feature: matchDefaultFeature,
      isDefault: true,
    }
  }

  if (matchAvaliableFeature) {
    return {
      feature: matchAvaliableFeature,
      isDefault: false,
    }
  }
}
